import { billingFeaturesServiceModule } from '../billing-features-service/billing-features-service';
import { appConfig } from '../../constants/app-config';

export const billingPlansServiceModule = angular
  .module('Q.services.billing-plans-service', [billingFeaturesServiceModule])
  .constant('CUSTOM_PLAN', {
    id: 'custom',
    description:
      'No matter what size organization you are, or what use case you have, we have plans designed for teams that will work for you.',
    planCategory: 'custom',
    planCategoryName: 'Team',
    basePrice: null,
    baseIntervalName: '12 Month',
    features: {
      baseFeatureSet: 'custom',
    },
  })

  .factory(
    'billingPlansService',
    function ($http, billingFeaturesService, CUSTOM_PLAN) {
      function planApi(plan, customer) {
        let api = angular.extend(plan, {
          featuresList: billingFeaturesService.catalog(plan.features),
          featureStatus(key) {
            const feature = this.featuresList[key];
            if (feature) {
              if (feature.label) {
                return 'labeled';
              }

              if (this.id === 'custom') {
                // if the custom plan and the feature is not a starter feature, then it's a premium not included
                // in all team based plans, so let's convey that via a different icon
                if (
                  !feature.enabled ||
                  (feature.featureSets &&
                    feature.featureSets.indexOf('growth') === -1)
                ) {
                  return 'optional';
                }
              }

              if (feature.enabled) {
                return 'enabled';
              }
            }

            return 'disabled';
          },
          pricingLabel() {
            if (plan.baseIntervalPriceFloat || 0 > 0) {
              return `${plan.basePriceFloat.toLocaleString('en')}`;
            } else if (plan.bulkPriceFloat || 0 > 0) {
              return `${plan.bulkPriceFloat.toLocaleString('en')}`;
            } else if (plan.burstUnitPriceFloat > 0) {
              return `${plan.burstUnitPriceFloat.toLocaleString('en')} per ${plan.usageMeasureUnitName}`;
            }
          },
          isBulk() {
            return (
              (plan.baseIntervalPriceFloat || 0) === 0 &&
              (plan.bulkPriceFloat || 0) > 0
            );
          },
          intervalLabel(type = 'base', ignoreBulk = false) {
            // if there is no base plan, and there is an up-front bulk price, then treat it as a bulk
            // plan. NOTE: we don't check planCategory == 'bulk' here because its possible for other plans,
            // such as "pilot", to also be bulk-like.
            if (!ignoreBulk && plan.isBulk()) {
              return 'Once';
            }

            let name = plan[`${type}IntervalName`] || '';
            switch (name) {
              case '1 Month':
                return 'Monthly';
              case '3 Month':
                return 'Quarterly';
              case '6 Month':
                return 'Semi-Annually';
              case '12 Month':
                return 'Annually';
              case '1 Week':
                return 'Weekly';
              case '2 Week':
                return 'Semi-Monthly';
              case '':
                return null;
              default:
                return `Every ${name}s`;
            }
          },
          creditsLabel() {
            if (plan.id === 'custom') {
              return 'Different Options Available';
            } else if (plan.recurringUsageAllotment === 0) {
              return 'Charged as Used';
            } else {
              let interval = (plan.usageIntervalName + 's')
                .replace('1 Months', 'Month')
                .replace('1 Weeks', 'Week');

              interval = 'Every ' + interval;
              interval = interval.replace('Every 12 Months', 'Annually');

              return `${plan.recurringUsageAllotment.toLocaleString('en')} ${plan.usageMeasureUnitName.replace('Assessment ', '')}s ${interval}`;
            }
          },
          ctaLabel() {
            if (plan.isCurrent) {
              return 'Current';
            } else if (plan.id === 'custom' || plan.isDisabled) {
              return 'Contact Us';
            } else if (!customer) {
              return 'Start Free Trial';
            } else if (plan.isScheduledDowngrade) {
              return 'Cancel Scheduled Downgrade';
            } else if (plan.id === 'custom' || plan.isDisabled) {
              return 'Contact Us';
            } else if (plan.isBulk()) {
              return 'Purchase';
            } else if (customer && customer.plan && customer.isPaying) {
              if (customer.plan.basePrice > plan.basePrice) {
                return 'Downgrade';
              }
              return 'Switch';
            }

            return 'Subscribe';
          },
        });

        // we also want to disable any customers under contract from making changes
        if (customer && customer.contractId) {
          api.isDisabled = true;
        }

        if (
          customer &&
          customer.scheduledChanges &&
          customer.scheduledPlan &&
          customer.scheduledPlan.billingPlanId === plan.id
        ) {
          api.isScheduledDowngrade = true;
        }

        return api;
      }

      let svc = {
        cache: null,
        extendedApi(customer) {
          let plans = [],
            customerPlan = false;

          let addCustomerPlan = (isPublic) => {
            // we want to make sure we don't add a duplicate plan, so try to find an existing one first
            let existing =
              customer.plan.billingPlanId &&
              plans.find((p) => p.id === customer.plan.billingPlanId);

            // need to make a copy and add the features to the plan, since customer plans store
            // features at the customer level
            const plan = existing || angular.copy(customer.plan);
            plan.id = customer.plan.billingPlanId;
            plan.features = customer.features;
            plan.public = isPublic;
            plan.isDisabled = customer.isPaying;
            plan.isCurrent = true;

            if (customer.state === 'non_renewing') {
              plan.cancelsAt = customer.currentBaseTermEnd;
            }

            if (customer.scheduledPlan) {
              plan.downgradesAt = customer.currentBaseTermEnd;
            }

            if (!existing) plans.push(plan);
            customerPlan = true;
          };

          // go through each available plan and decide if we should replace it with the current plan
          svc.cache.forEach((plan) => {
            if (
              customer &&
              customer.plan &&
              customer.plan.planCategory === plan.planCategory &&
              !customerPlan &&
              customer.isPaying
            ) {
              addCustomerPlan(true);
            } else if (!plans.find((p) => p.id === plan.id)) {
              plans.push(plan);
            }
          });

          // if the customer has a plan and it hasn't been added yet, then it must be a non-standard plan
          if (customer && customer.plan && !customerPlan && customer.isPaying) {
            addCustomerPlan();
          }

          // add custom plan if there are only 1 or 2 plans available, and the last plan is not an enteprise plan
          if (
            !plans.length ||
            (plans.length < 3 &&
              plans[plans.length - 1].planCategory !== 'enterprise')
          ) {
            plans.push(CUSTOM_PLAN);
          }

          plans = plans.map((plan) => {
            const copy = angular.copy(plan);
            return planApi(copy, customer);
          });

          return plans.sort((a, b) => {
            return a.basePrice + a.bulkPrice > b.basePrice + b.bulkPrice
              ? 1
              : -1;
          });
        },
        load(customer) {
          return $http
            .get(`${appConfig.apiRoot}billing/plans`)
            .then((response) => {
              svc.cache = (response.data && response.data.data) || [];
              return svc.extendedApi(customer);
            });
        },
      };

      return svc;
    },
  );
